import React, { useEffect } from "react";
import {
  Avatar,
  Box,
  ClickAwayListener,
  IconButton,
  MenuItem,
  MenuList,
  Pagination,
  Paper,
  Popper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { Grid } from "@mui/system";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import LoopIcon from "@mui/icons-material/Loop";
import PendingIcon from "@mui/icons-material/Pending";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import store from "../../../../../store";
import { getWorkspaceImage } from "../../../../../core/layouts/shared/workspaceImages";
import { colors, spacing, typography } from "../../../../../assets/styles/tokens";
import { selectUserToken, setDisplayErrorSnackBar } from "../../../../../features/userSlice";
import { BrandsEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import { useCopyToClipboard } from "../brandPropertiesHook/useCopyToClipboard";
import { formatDateTime } from "../utilitiesOfBrandConfig/helperOfBrandConfig";
import {
  ACCOUNT_NAME_MAX_CHARS,
  DEFAULT_BRANDS_SORT_DIR,
  DEFAULT_BRANDS_SORT_KEY,
  STUCK_AT_LABELS,
  WEBSITE_NAME_MAX_CHARS,
  truncateWithEllipsis,
} from "../utils/helpersOfBrands";
import { capitalizeEachWords } from "../../../../../utils/helpers";
import RunBrandData from "../BrandConfigurationOverview/RunModals/RunBrandData/RunBrandData";
import BrandsStatusBadge from "../BrandsStatusBadge";
import BrandsQuickActions from "../BrandsQuickActions";
import IndustryBadge from "../IndustryBadge";
import { openRunBrandModal } from "../utils/ensureRunProcessSupportData";
import { useAutomaticConfigure } from "../utils/useAutomaticConfigure";
import { useBrands } from "../utils/useBrands";

interface Props {
  rows: any[];
}

interface TableHeader {
  id: number;
  key: string;
  label: string;
  sortable: boolean;
  // Floor for a column's width. Status carries one so the column never
  // collapses narrower than its widest badge ("Account Issue" + its
  // sub-label), which is 160px in Figma.
  minWidth?: number;
}

const tableHeaders: TableHeader[] = [
  { id: 0, key: "brand_name", label: "Brand Name", sortable: true },
  { id: 1, key: "brand_id", label: "Brand ID", sortable: true },
  { id: 2, key: "account_username", label: "Account", sortable: true },
  { id: 3, key: "company_name", label: "Company", sortable: true },
  { id: 4, key: "affiliate_system", label: "Affiliate System", sortable: true },
  { id: 5, key: "website_name", label: "Website", sortable: true },
  { id: 6, key: "status", label: "Status", sortable: true, minWidth: 160 },
  { id: 7, key: "quick_actions", label: "Quick Actions", sortable: false },
];

const CopyKeeperIdIcon = ({
  keeperId,
  copied,
  onCopy,
}: {
  keeperId?: string;
  copied: boolean;
  onCopy: () => void;
}) => (
  <IconButton
    size="small"
    disabled={!keeperId}
    color={copied ? "success" : "secondary"}
    onClick={(e) => {
      e.stopPropagation();
      onCopy();
    }}
  >
    {copied ? (
      <CheckCircleOutlinedIcon fontSize="inherit" />
    ) : (
      <ContentCopyIcon fontSize="inherit" />
    )}
  </IconButton>
);

// Opens the brand's own login_url (from the API) in a new tab. Always
// visible: disabled/grey when there's no login_url, secondary-colored and
// clickable when there is one.
const OpenLoginUrlIcon = ({ loginUrl }: { loginUrl?: string }) => (
  <Tooltip title={loginUrl ? "Open login URL" : ""}>
    <span>
      {loginUrl ? (
        <RouterLink to={loginUrl} target="_blank" onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" color="secondary">
            <OpenInNewIcon fontSize="inherit" />
          </IconButton>
        </RouterLink>
      ) : (
        <IconButton size="small" disabled>
          <OpenInNewIcon fontSize="inherit" />
        </IconButton>
      )}
    </span>
  </Tooltip>
);

// Matches the site icon/image styling used by the workspace switcher dropdown
// in the drawer (WorkspaceDropdown.tsx) — same image lookup, same square Avatar.
const WebsiteIcon = ({ websiteName }: { websiteName?: string }) => {
  const image = getWorkspaceImage(websiteName);
  return (
    <Avatar
      src={image || undefined}
      sx={{
        bgcolor: image ? "transparent" : colors.Brand[700].$value,
        color: image ? "transparent" : colors.Base.White.$value,
        width: spacing.Spacing["4xl"].$value,
        height: spacing.Spacing["4xl"].$value,
        borderRadius: spacing.Radius.xs.$value,
        fontSize: typography.Size.Text.sm.$value,
        fontWeight: typography.Font_weight.Medium.$value,
      }}
    >
      {!image && websiteName?.charAt(0)?.toUpperCase()}
    </Avatar>
  );
};

const AffiliateSystemCell = ({
  affiliateSystem,
  loginUrl,
  keeperId,
}: {
  affiliateSystem?: string;
  loginUrl?: string;
  keeperId?: string;
}) => {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Stack spacing={0.25}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {loginUrl ? (
          <Typography
            component={RouterLink}
            to={loginUrl}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            fontSize="14px"
            sx={{ color: "secondary.main", textDecoration: "none" }}
          >
            {affiliateSystem ?? "N/A"}
          </Typography>
        ) : (
          <Typography fontSize="14px">{affiliateSystem ?? "N/A"}</Typography>
        )}
        <OpenLoginUrlIcon loginUrl={loginUrl} />
      </Stack>
      {/* Single Tooltip over the whole icon+text unit — same pattern as the
          Copy Keeper ID control on the Brand Overview page — so hovering
          either the icon or the text shows "Copy Keeper ID". */}
      <Tooltip title={!keeperId ? "" : copied ? "Copied!" : "Copy Keeper ID"}>
        {/* Tracker sits on the wrapper, not the IconButton, so clicking
            either the icon or the "Keeper ID" label reports the same action
            (trackingHandler resolves the nearest [data-tracker] ancestor). */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          data-tracker="1071"
          onClick={(e) => {
            e.stopPropagation();
            keeperId && copy(keeperId);
          }}
          sx={{ width: "fit-content", cursor: keeperId ? "pointer" : "default" }}
        >
          <CopyKeeperIdIcon
            keeperId={keeperId}
            copied={copied}
            onCopy={() => keeperId && copy(keeperId)}
          />
          <Typography
            fontSize="11px"
            sx={{
              textDecoration: "underline",
              color: keeperId ? "secondary.main" : "text.disabled",
            }}
          >
            Keeper ID
          </Typography>
        </Stack>
      </Tooltip>
    </Stack>
  );
};

// Icon/color per integration_status value, matching the legacy
// BrandConfigurationReportTableData.tsx Alert icons exactly (Ready/Not
// Ready/Running/Pending — a different vocabulary from the Status column's
// BrandsStatusBadge, which uses synced/config_issue/... instead).
const INTEGRATION_STATUS_ICONS: Record<string, { icon: JSX.Element; color: string }> = {
  Ready: { icon: <CheckCircleIcon fontSize="small" />, color: "#2E7D32" },
  "Not Ready": { icon: <ErrorIcon fontSize="small" />, color: "#D32F2F" },
  Running: { icon: <LoopIcon fontSize="small" />, color: "#7B1FA2" },
  Pending: { icon: <PendingIcon fontSize="small" />, color: "#0288D1" },
};

const IntegrationStatusValue = ({ integrationStatus }: { integrationStatus?: string | null }) => {
  const config = integrationStatus ? INTEGRATION_STATUS_ICONS[integrationStatus] : undefined;
  if (!config) return <>{integrationStatus ?? "N/A"}</>;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <span style={{ display: "flex", color: config.color }}>{config.icon}</span>
      <span>{integrationStatus}</span>
    </Stack>
  );
};

const DetailField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Grid size={3}>
    <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Grid>
);

const ROWS_PER_PAGE_OPTIONS = [10, 25, 100];

// Custom "Rows per page" control replacing MUI's Select-based one. MUI's
// Select/Menu is built on Popover -> Modal, which always renders a
// full-viewport backdrop to catch outside clicks — making that backdrop
// pointer-events-transparent (to let checkbox clicks through) also breaks
// its own click-to-close behavior, since it needs to receive the click to
// close. Popper has no backdrop at all, so a click on a checkbox behind it
// reaches the checkbox completely normally, while ClickAwayListener (a
// plain event listener, not an overlay) still detects the outside click and
// closes the dropdown without consuming it.
const RowsPerPageControl = ({
  value,
  onChange,
  count,
  page,
}: {
  value: number;
  onChange: (value: number) => void;
  count: number;
  page: number;
}) => {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);

  const from = count === 0 ? 0 : page * value + 1;
  const to = Math.min(count, (page + 1) * value);

  return (
    <Stack direction="row" alignItems="center">
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          Rows per page:
        </Typography>
        <Box
          ref={anchorRef}
          onClick={() => setOpen((prev) => !prev)}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
        >
          <Typography variant="body2">{value}</Typography>
          <ArrowDropDownIcon fontSize="small" />
        </Box>
        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper elevation={3}>
              <MenuList dense>
                {ROWS_PER_PAGE_OPTIONS.map((option) => (
                  <MenuItem
                    key={option}
                    selected={option === value}
                    onClick={() => {
                      onChange(option);
                      setOpen(false);
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
        {from}&ndash;{to} of {count}
      </Typography>
    </Stack>
  );
};

// Needs Configuration table: no checkbox, no bulk action. Clicking a row
// expands an inline detail panel (Integration Status / Stuck At / Reason /
// Last Run), per spec. Quick actions and the Run Process/Reconfigure wiring
// mirror AllBrandsTable.tsx exactly (same shared components, same reuse of
// the legacy Run Process wizard).
const NeedsConfigurationTable = ({ rows }: Props) => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const { isModalOpen } = useSelector((state: any) => state.brandConfigurationReport);
  const { fetchedBrandsData, beginLoading, endLoading } = useBrands();
  const [searchParams, setSearchParams] = useSearchParams();

  // No ?nc_sort= in the URL -> default Brand ID, newest first.
  const [sortKey, setSortKey] = React.useState<string | null>(
    () => searchParams.get("nc_sort") ?? DEFAULT_BRANDS_SORT_KEY,
  );
  const [sortDir, setSortDir] = React.useState<"asc" | "desc" | null>(() =>
    searchParams.get("nc_sort")
      ? ((searchParams.get("nc_dir") as "asc" | "desc" | null) ?? null)
      : DEFAULT_BRANDS_SORT_DIR,
  );
  const [page, setPage] = React.useState<number>(() => {
    const fromUrl = Number(searchParams.get("nc_page"));
    return Number.isInteger(fromUrl) && fromUrl >= 0 ? fromUrl : 0;
  });
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { openAutomaticConfigure, automaticConfigureDialogs } = useAutomaticConfigure();
  const [expandedBrandId, setExpandedBrandId] = React.useState<number | null>(null);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // The default sort stays out of the URL, same as page 0.
        const isDefaultSort =
          sortKey === DEFAULT_BRANDS_SORT_KEY && sortDir === DEFAULT_BRANDS_SORT_DIR;
        if (sortKey && !isDefaultSort) next.set("nc_sort", sortKey);
        else next.delete("nc_sort");
        if (sortDir && !isDefaultSort) next.set("nc_dir", sortDir);
        else next.delete("nc_dir");
        if (page) next.set("nc_page", String(page));
        else next.delete("nc_page");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDir, page]);

  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;
    if (sortKey === header.key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(header.key);
      setSortDir("asc");
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!sortKey || !sortDir) return rows;
    return [...rows].sort((a, b) => {
      const aValue = a?.[sortKey];
      const bValue = b?.[sortKey];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDir === "asc" ? aValue - bValue : bValue - aValue;
      }
      return sortDir === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [rows, sortKey, sortDir]);

  const paginatedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleRunBrand = (row: any) => {
    openRunBrandModal(oauthTokenHeader, [
      { brandId: row.brand_id, brandName: row.brand_name },
    ]);
  };

  const handleTryToConnect = async (row: any) => {
    // Show the same whole-tab BrandsSkeleton used on first load for the
    // entire operation (the connect request itself, not just the refetch
    // after it succeeds).
    beginLoading();
    let didRefetch = false;
    try {
      const response = await BrandsEndPointLogic.tryToConnect(oauthTokenHeader, [
        row.brand_id,
      ]);
      // 207 = partial success (warning snackbar already shown by
      // BrandsEndPointLogic.tryToConnect) — still refresh for the ones that
      // connected.
      if (response?.status === 200 || response?.status === 207) {
        if (response.status === 200) {
          store.dispatch(
            setDisplayErrorSnackBar({
              message: `${row.brand_name} connect request sent.`,
              type: "success",
            }),
          );
        }
        // Refresh so the brand's updated status/tab membership shows up —
        // fetchedBrandsData() ends the loading state itself once done.
        await fetchedBrandsData();
        didRefetch = true;
      } else {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: `Failed to connect ${row.brand_name}.`,
            type: "error",
          }),
        );
      }
    } catch {
      // BrandsEndPointLogic.tryToConnect already dispatches the error snackbar.
    } finally {
      if (!didRefetch) endLoading();
    }
  };

  const toggleExpanded = (brandId: number) => {
    setExpandedBrandId((prev) => (prev === brandId ? null : brandId));
  };

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {tableHeaders.map((header) => (
              <TableCell key={header.id} sx={{ minWidth: header.minWidth }}>
                <TableSortLabel
                  active={sortKey === header.key}
                  direction={sortKey === header.key ? sortDir ?? "asc" : "asc"}
                  onClick={() => handleHeaderClick(header)}
                  disabled={!header.sortable}
                >
                  {header.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={tableHeaders.length}
                align="center"
                sx={{ padding: "20px", color: "#888", fontSize: "16px" }}
              >
                No Data To Display
              </TableCell>
            </TableRow>
          ) : (
            paginatedRows.map((row: any) => (
              <React.Fragment key={row.brand_id}>
                {/* Row-level tracker: any click inside the row reports 1073
                    unless it lands on a more specific tracked control (quick
                    actions, Copy Keeper ID), which takes precedence because
                    trackingHandler resolves the nearest [data-tracker]
                    ancestor. Here the row click also expands the detail
                    panel. */}
                <TableRow
                  hover
                  data-tracker="1073"
                  onClick={() => toggleExpanded(row.brand_id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography fontSize="14px">{row.brand_name}</Typography>
                    <IndustryBadge industryName={row.industry_name} />
                  </TableCell>
                  <TableCell>{row.brand_id ?? "N/A"}</TableCell>
                  <TableCell>
                    {row.account_username ? (
                      <Tooltip
                        title={row.account_username}
                        placement="top"
                        arrow
                      >
                        <span>
                          {truncateWithEllipsis(
                            row.account_username,
                            ACCOUNT_NAME_MAX_CHARS,
                          )}
                        </span>
                      </Tooltip>
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                  <TableCell>{row.company_name ?? "N/A"}</TableCell>
                  <TableCell>
                    <AffiliateSystemCell
                      affiliateSystem={row.affiliate_system}
                      loginUrl={row.login_url}
                      keeperId={row.keeper_id}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <WebsiteIcon websiteName={row.website_name} />
                      <Box>
                        <Typography fontSize="14px">
                          {row.website_name
                            ? truncateWithEllipsis(
                                row.website_name,
                                WEBSITE_NAME_MAX_CHARS,
                              )
                            : "N/A"}
                        </Typography>
                        {row.website_alias && (
                          <Typography fontSize="12px" color="text.secondary">
                            {row.website_alias}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <BrandsStatusBadge
                      status={row.status}
                      brandIsActive={row.brand_is_active}
                      accountStatusName={row.account_status_name}
                    />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <BrandsQuickActions
                      row={row}
                      onRunBrand={handleRunBrand}
                      onReconfigure={openAutomaticConfigure}
                      onTryToConnect={handleTryToConnect}
                    />
                  </TableCell>
                </TableRow>
                {expandedBrandId === row.brand_id && (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length} sx={{ backgroundColor: "#FAFAFA" }}>
                      <Grid container spacing={2}>
                        <DetailField
                          label="Integration Status"
                          value={<IntegrationStatusValue integrationStatus={row.integration_status} />}
                        />
                        <DetailField
                          label="Stuck At"
                          value={
                            row.stuck_step
                              ? (STUCK_AT_LABELS[row.stuck_step] ??
                                capitalizeEachWords(row.stuck_step))
                              : "N/A"
                          }
                        />
                        <DetailField
                          label="Reason"
                          value={row.reason ?? "N/A"}
                        />
                        <DetailField
                          label="Last Run"
                          value={row.last_run ? formatDateTime(row.last_run) : "N/A"}
                        />
                      </Grid>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
        }}
      >
        <Box sx={{ justifySelf: "start" }}>
          <RowsPerPageControl
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            count={sortedRows.length}
            page={page}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(sortedRows.length / rowsPerPage))}
          page={page + 1}
          onChange={(_, value) => handleChangePage(null, value - 1)}
          showFirstButton
          showLastButton
          color="secondary"
          size="small"
          sx={{ justifySelf: "center" }}
        />
        <Box />
      </Box>

      <RunBrandData open={isModalOpen} />
      {automaticConfigureDialogs}
    </TableContainer>
  );
};

export default NeedsConfigurationTable;
